import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import AdminLayout from './layouts/AdminLayout';
import { 
  Mail, 
  Briefcase, 
  Calendar, 
  Eye, 
  Trash2, 
  Search,
  Filter,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  FileText,
  Users,
  UserCheck,
  UserX,
  Download
} from "lucide-react";
import { useQuery, useQueryClient } from "react-query";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const fetchJobs = async () => {
  const response = await fetch(`${API_URL}/jobs`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data.jobs) ? data.jobs : [];
};

const fetchAllApplications = async () => {
  const jobsData = await fetchJobs();
  let allApplications = [];

  try {
    console.log('Starting to fetch applications...');
    
    // First try to fetch all applications at once (more efficient)
    try {
      const allResponse = await fetch(`${API_URL}/applications`);
      if (allResponse.ok) {
        const allData = await allResponse.json();
        console.log('All applications response:', allData);
        
        if (allData && allData.success === true && Array.isArray(allData.applications)) {
          // New API structure with applications array in response object
          console.log(`Found ${allData.applications.length} applications in new API format`);
          allApplications = [...allData.applications];
          
          // Return early - we have all applications
          return allApplications.sort((a, b) => {
            const dateA = a.appliedDate || a.createdAt;
            const dateB = b.appliedDate || b.createdAt;
            return new Date(dateB) - new Date(dateA);
          });
        }
      }
    } catch (error) {
      console.error('Error fetching all applications:', error);
      // Continue with fallback approach
    }
    
    // Fallback: First try to fetch general applications
    console.log('Falling back to separate general/job-specific fetches');
    const generalResponse = await fetch(`${API_URL}/applications?isGeneral=true`);
    if (!generalResponse.ok) {
      console.error('Failed to fetch general applications:', await generalResponse.text());
    } else {
      const generalData = await generalResponse.json();
      console.log('General applications response:', generalData);
      
      // Check if the response has applications property (new API structure)
      if (generalData && generalData.applications && Array.isArray(generalData.applications)) {
        console.log(`Found ${generalData.applications.length} general applications in new API format`);
        allApplications = [...allApplications, ...generalData.applications];
      } else if (Array.isArray(generalData)) {
        // Fallback for old API structure
        console.log(`Found ${generalData.length} general applications in old API format`);
        allApplications = [...allApplications, ...generalData];
      }
    }

    // Then fetch job-specific applications
    console.log(`Fetching applications for ${jobsData.length} jobs`);
    for (const job of jobsData) {
      try {
        const jobResponse = await fetch(`${API_URL}/applications?jobId=${job._id}`);
        if (jobResponse.ok) {
          const jobData = await jobResponse.json();
          console.log(`Job ${job._id} applications response:`, jobData);
          
          // Check if the response has applications property (new API structure)
          if (jobData && jobData.applications && Array.isArray(jobData.applications)) {
            console.log(`Found ${jobData.applications.length} applications for job ${job._id} in new API format`);
            allApplications = [...allApplications, ...jobData.applications];
          } else if (Array.isArray(jobData)) {
            // Fallback for old API structure
            console.log(`Found ${jobData.length} applications for job ${job._id} in old API format`);
            allApplications = [...allApplications, ...jobData];
          }
        }
      } catch (error) {
        console.error(`Error fetching applications for job ${job._id}:`, error);
      }
    }

    console.log('All applications fetched:', allApplications.length);
    
    // Sort all applications by date (using createdAt as fallback for appliedDate)
    return allApplications.sort((a, b) => {
      const dateA = a.appliedDate || a.createdAt;
      const dateB = b.appliedDate || b.createdAt;
      return new Date(dateB) - new Date(dateA);
    });
  } catch (error) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'Reviewed': { color: 'bg-blue-100 text-blue-800', icon: Eye },
    'Interviewed': { color: 'bg-purple-100 text-purple-800', icon: UserCheck },
    'Offered': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    'Rejected': { color: 'bg-red-100 text-red-800', icon: XCircle },
  };

  const config = statusConfig[status] || statusConfig['Pending'];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </span>
  );
};

const ApplicationCard = ({ application, jobs, onStatusChange, onDelete }) => {
  const getJobTitle = () => {
    if (application.isGeneralApplication) {
      return "General Application";
    }
    // If jobId is a populated object with a title, use it
    if (application.jobId && typeof application.jobId === 'object' && application.jobId.title) {
      return application.jobId.title;
    }
    // Otherwise, try to find in jobs array
    const jobId = application.jobId?._id || application.jobId;
    const job = jobs.find(j => j._id === jobId);
    return job?.title || "Job Deleted";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {application.firstName} {application.lastName}
                </h3>
                <p className="text-sm text-gray-500">{application.email}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <StatusBadge status={application.status || 'Pending'} />
            <select
              value={application.status || 'Pending'}
              onChange={(e) => onStatusChange(application._id, e.target.value)}
              className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-primary focus:border-primary"
            >
              <option value="Pending">Pending</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Interviewed">Interviewed</option>
              <option value="Offered">Offered</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Briefcase className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{getJobTitle()}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span>Applied {new Date(application.appliedDate).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to={`/admin/applications/${application._id}`}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Eye className="w-4 h-4 mr-1" />
            View Details
          </Link>
          
          <button
            onClick={() => onDelete(application._id, `${application.firstName} ${application.lastName}`)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    red: "bg-red-50 text-red-700 border-red-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200"
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center">
        <Icon className="w-8 h-8 mr-3" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm font-medium">{title}</p>
        </div>
      </div>
    </div>
  );
};

const AdminApplications = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: jobs = [],
    isLoading: isJobsLoading,
    error: jobsError,
  } = useQuery("jobs", fetchJobs);

  const {
    data: applications = [],
    isLoading: isApplicationsLoading,
    error: applicationsError,
    refetch: refetchApplications
  } = useQuery("applications", fetchAllApplications);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetchApplications();
    setRefreshing(false);
  };

  const handleStatusChange = useCallback(
    async (applicationId, newStatus) => {
      try {
        const response = await fetch(
          `${API_URL}/applications/${applicationId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status: newStatus }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        queryClient.setQueryData("applications", (oldData) =>
          oldData.map((app) =>
            app._id === applicationId ? { ...app, status: newStatus } : app
          )
        );
      } catch (error) {
        console.error("Error updating application status:", error);
      }
    },
    [queryClient]
  );

  const handleDeleteApplication = useCallback(
    async (applicationId, applicantName) => {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete the application from ${applicantName}? This action cannot be undone.`
      );
      
      if (!confirmDelete) return;

      try {
        const response = await fetch(
          `${API_URL}/applications/${applicationId}`,
          {
            method: "DELETE",
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Application deleted:", result);

        // Remove the application from the local state
        queryClient.setQueryData("applications", (oldData) =>
          oldData.filter((app) => app._id !== applicationId)
        );

        alert("Application deleted successfully");
      } catch (error) {
        console.error("Error deleting application:", error);
        alert("Error deleting application. Please try again.");
      }
    },
    [queryClient]
  );

  const filteredApplications = applications.filter((app) => {
    const matchesSearch =
      app.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || 
      (app.status && app.status.toLowerCase() === statusFilter.toLowerCase());
    const matchesJob =
      jobFilter === "all" ||
      (jobFilter === "general" && app.isGeneralApplication) ||
      app.jobId === jobFilter;
    return matchesSearch && matchesStatus && matchesJob;
  });

  // Calculate statistics
  const stats = {
    total: applications.length,
    pending: applications.filter(app => !app.status || app.status === 'Pending').length,
    reviewed: applications.filter(app => app.status === 'Reviewed').length,
    interviewed: applications.filter(app => app.status === 'Interviewed').length,
    offered: applications.filter(app => app.status === 'Offered').length,
    rejected: applications.filter(app => app.status === 'Rejected').length,
  };

  if (isJobsLoading || isApplicationsLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (jobsError || applicationsError) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">Error loading applications data. Please try again.</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | Applications Management</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Applications Management</h1>
              <p className="text-sm text-gray-500 mt-1">Review and manage job applications</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <StatCard title="Total" value={stats.total} icon={Users} color="blue" />
          <StatCard title="Pending" value={stats.pending} icon={Clock} color="yellow" />
          <StatCard title="Reviewed" value={stats.reviewed} icon={Eye} color="blue" />
          <StatCard title="Interviewed" value={stats.interviewed} icon={UserCheck} color="purple" />
          <StatCard title="Offered" value={stats.offered} icon={CheckCircle} color="green" />
          <StatCard title="Rejected" value={stats.rejected} icon={UserX} color="red" />
        </div>

        {/* Alert for pending applications */}
        {stats.pending > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  You have <strong>{stats.pending}</strong> application{stats.pending > 1 ? 's' : ''} waiting for review.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="py-2 pl-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="interviewed">Interviewed</option>
                <option value="offered">Offered</option>
                <option value="rejected">Rejected</option>
              </select>
              
              <select
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                className="py-2 pl-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="all">All Jobs</option>
                <option value="general">General Applications</option>
                {jobs.map((job) => (
                  <option key={job._id} value={job._id}>
                    {job.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No applications found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== "all" || jobFilter !== "all"
                ? "Try adjusting your search or filter criteria." 
                : "No applications have been submitted yet."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredApplications.map((application) => (
              <ApplicationCard
                key={application._id}
                application={application}
                jobs={jobs}
                onStatusChange={handleStatusChange}
                onDelete={handleDeleteApplication}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
